import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { Typography } from '../typography';
import { colors } from '../../const';
import { i18n } from '../../i18n';
import { Card, CardHeader } from './Components';

interface DonutPanelProps {
  title: string;
  categories: { name: string; value: number }[];
  centerLabel?: string;
}

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 18rem 1fr;
  column-gap: var(--spacing-4);
  align-items: center;
  min-width: 0;
`;

const ChartGrid = styled.div`
  display: grid;
  place-items: center;
  min-width: 0;
  min-height: 18rem;
`;

const ChartLayer = styled.div`
  grid-column: 1;
  grid-row: 1;
  width: 100%;
  min-width: 0;
  height: 18rem;
  min-height: 18rem;
  box-sizing: border-box;
`;

const CenterText = styled.div`
  grid-column: 1;
  grid-row: 1;
  display: grid;
  place-items: center;
  pointer-events: none;
`;

const CenterTextInner = styled.div`
  display: grid;
  place-items: center;
  row-gap: 0.2rem;
`;

const Legend = styled.div`
  display: grid;
  row-gap: var(--spacing-4);
`;

const LegendRow = styled.div`
  display: grid;
  grid-template-columns: 1.2rem 1fr auto;
  column-gap: 0.8rem;
  align-items: center;
`;

const Swatch = styled.div<{ $color: string }>`
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 0.2rem;
  background: ${({ $color }) => $color};
`;

const RightValue = styled.div`
  display: grid;
  justify-items: end;
`;

const defaultSliceColors = [
  colors['--green-100'],
  colors['--yellow-100'],
  colors['--primary-100'],
  colors['--secondary-100'],
  colors['--gray-60'],
  colors['--gray-40'],
];

/** До первого ResizeObserver Recharts v3 держит −1×−1 → предупреждение при width/height «100%». ~18rem при root 10px. */
const DONUT_CHART_INITIAL_DIMENSION = { width: 180, height: 180 } as const;

export const DonutPanel = ({
  title,
  categories,
  centerLabel = i18n.t('label.total'),
}: DonutPanelProps) => {
  const safeCategories = categories?.filter((c) => Number.isFinite(c.value) && c.value > 0) ?? [];
  const total = safeCategories.reduce((acc, c) => acc + c.value, 0);

  return (
    <Card>
      <CardHeader title={title} />

      <ContentGrid>
        <ChartGrid>
          <ChartLayer>
            <ResponsiveContainer
              width="100%"
              height="100%"
              initialDimension={DONUT_CHART_INITIAL_DIMENSION}
            >
              <PieChart>
                <Pie
                  data={safeCategories}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="68%"
                  outerRadius="92%"
                  paddingAngle={2}
                  stroke="transparent"
                  isAnimationActive={false}
                >
                  {safeCategories.map((_, idx) => (
                    <Cell key={idx} fill={defaultSliceColors[idx % defaultSliceColors.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </ChartLayer>

          <CenterText>
            <CenterTextInner>
              <Typography variant="h5" weight="bold" color="--gray-100">
                {total.toLocaleString('en-US')}
              </Typography>
              <Typography variant="textM" weight="normal" color="--gray-70">
                {centerLabel}
              </Typography>
            </CenterTextInner>
          </CenterText>
        </ChartGrid>

        <Legend>
          {safeCategories.map((c, idx) => {
            const percent = total > 0 ? (c.value / total) * 100 : 0;
            return (
              <LegendRow key={`${c.name}-${idx}`}>
                <Swatch $color={defaultSliceColors[idx % defaultSliceColors.length]} />
                <Typography variant="textXS" weight="normal" color="--gray-90">
                  {c.name}
                </Typography>
                <RightValue>
                  <Typography variant="textXS" weight="normal" color="--gray-90">
                    {c.value.toLocaleString('en-US')} ({percent.toFixed(1)}%)
                  </Typography>
                </RightValue>
              </LegendRow>
            );
          })}
        </Legend>
      </ContentGrid>
    </Card>
  );
};
